import _, { update } from "lodash";
import UUID from "uuid/v4";

export const editLibrary = (
  userId,
  appId,
  currentLib,
  oldCategory,
  updatedCategory
) => {
  if (!updatedCategory.id) {
    updatedCategory.categoryId = UUID();
  }

  updatedCategory.modifiedAt = { nanos: 0, seconds: new Date().getTime() };

  let updatedGroups = currentLib.groups.map((group) => {
    let uGrp = group;

    uGrp.categories = group.categories.map((category) => {
      if (category.categoryId === updatedCategory.id) {
        //Return the updated category instead of the original
        return formatCategoryFromPageResp(updatedCategory, oldCategory.library);
      }
      return category;
    });

    return uGrp;
  });

  let updatedTemplate = {
    appId: appId,
    template: {
      groups: updatedGroups,
      header: currentLib.header,
      templateId: currentLib.templateId,
    },
  };

  return updatedTemplate;
};

const findCategoryInGroup = (group, groupIndex, deleteableLib) => {
  if (!group || !group.categories) {
    return null;
  }

  const { categoryIndex, categoryId, library } = deleteableLib;
  const categories = group.categories;

  if (
    categoryIndex != null &&
    categoryIndex >= 0 &&
    categoryIndex < categories.length &&
    categoryId &&
    categories[categoryIndex].categoryId === categoryId
  ) {
    return { groupIndex, categoryIndex };
  }

  if (categoryId) {
    const ci = categories.findIndex((c) => c.categoryId === categoryId);
    if (ci >= 0) {
      return { groupIndex, categoryIndex: ci };
    }
  }

  if (library) {
    const ci = categories.indexOf(library);
    if (ci >= 0) {
      return { groupIndex, categoryIndex: ci };
    }
  }

  return null;
};

const findCategoryLocation = (groups, deleteableLib) => {
  const { groupId, groupIndex, categoryId, library } = deleteableLib;

  if (groupId) {
    const gi = groups.findIndex((g) => g.groupId === groupId);
    if (gi >= 0) {
      const loc = findCategoryInGroup(groups[gi], gi, deleteableLib);
      if (loc) {
        return loc;
      }
    }
    return null;
  }

  if (groupIndex != null && groupIndex >= 0 && groupIndex < groups.length) {
    const loc = findCategoryInGroup(groups[groupIndex], groupIndex, deleteableLib);
    if (loc) {
      return loc;
    }
  }

  if (categoryId) {
    const matches = [];
    groups.forEach((group, gi) => {
      const ci = group.categories.findIndex((c) => c.categoryId === categoryId);
      if (ci >= 0) {
        matches.push({ groupIndex: gi, categoryIndex: ci });
      }
    });

    if (matches.length === 1) {
      return matches[0];
    }

    if (matches.length > 1 && library) {
      for (const match of matches) {
        if (groups[match.groupIndex].categories[match.categoryIndex] === library) {
          return match;
        }
      }
    }
  }

  if (library) {
    for (let gi = 0; gi < groups.length; gi++) {
      const ci = groups[gi].categories.indexOf(library);
      if (ci >= 0) {
        return { groupIndex: gi, categoryIndex: ci };
      }
    }
  }

  return null;
};

const deleteOld = (libraries, oldLibrary) => {
  if (!libraries || !libraries.groups) {
    return null;
  }

  const updated = _.cloneDeep(libraries);
  const location = findCategoryLocation(updated.groups, oldLibrary);

  if (!location) {
    return null;
  }

  const { groupIndex, categoryIndex } = location;
  updated.groups[groupIndex].categories.splice(categoryIndex, 1);

  if (updated.groups[groupIndex].categories.length === 0) {
    updated.groups.splice(groupIndex, 1);
  }

  return updated;
};

export const saveNewLibrary = (userId, appId, libraries, newLibrary) => {
  let appTemplate = {
    templateId: libraries.templateId,
    appId,
    template: {
      header: libraries.header,
      expanded: true,
      groups: [],
    },
  };

  const newLibraries = updateLibrariesWithNewLibrary(libraries, newLibrary);

  newLibraries.groups.forEach((group, index) => {
    if (group.categories.length !== 0) {
      appTemplate.template.groups.push(group);
    }
  });

  let isValid = true;
  appTemplate.template.groups.forEach((group, index) => {
    // check if group name is valid
    if (_.isEmpty(group.groupName)) {
      isValid = false;
      return;
    } else {
      // check if all the categories are valid
      group.categories.forEach((category) => {
        if (_.isEmpty(category.categoryName)) {
          isValid = false;
          return;
        }
      });
    }
  });

  if (isValid) {
    return appTemplate;
  } else {
    return null;
  }
};

const updateLibrariesWithNewLibrary = (currentLib, newOrUpdatedCategory) => {
  const grpIdx = getGroupIndexFromLibrary(
    currentLib,
    newOrUpdatedCategory.group ? newOrUpdatedCategory.group : ""
  );

  //If the user manage to skip adding a group -- return null and we will show an error higher up
  if (grpIdx.group === "") return;

  if (grpIdx === -1) {
    let emptyLibrary = {
      groupName: newOrUpdatedCategory.group,
      categories: [
        {
          categoryId: UUID(),
          groupName: newOrUpdatedCategory.group,
          categoryName: newOrUpdatedCategory.name,
          expanded: false,
          categoryDesc: newOrUpdatedCategory.description,
          categoryDescWithFormat: newOrUpdatedCategory.descriptionWithFormat,
          createdAt: { nanos: 0, seconds: new Date().getTime() },
          modifiedAt: { nanos: 0, seconds: new Date().getTime() },
        },
      ],
    };
    if (newOrUpdatedCategory.imageUrl !== "") {
      emptyLibrary.categories[0].imageUrl = newOrUpdatedCategory.imageUrl;
    }
    if (newOrUpdatedCategory.videoUrl !== "") {
      emptyLibrary.categories[0].videoUrl = newOrUpdatedCategory.videoUrl;
    }
    if (newOrUpdatedCategory.youtubeVideo !== "") {
      emptyLibrary.categories[0].youtubeVideo =
        newOrUpdatedCategory.youtubeVideo;
    }
    if (newOrUpdatedCategory.wistiaVideo !== "") {
      emptyLibrary.categories[0].wistiaVideo = newOrUpdatedCategory.wistiaVideo;
    }

    if (newOrUpdatedCategory.defaultReps !== "") {
      emptyLibrary.categories[0].defaultReps = newOrUpdatedCategory.defaultReps;
    }
    if (newOrUpdatedCategory.defaultSets !== "") {
      emptyLibrary.categories[0].defaultSets = newOrUpdatedCategory.defaultSets;
    }
    if (newOrUpdatedCategory.defaultDuration !== "") {
      emptyLibrary.categories[0].defaultDuration =
        newOrUpdatedCategory.defaultDuration;
    }
    if (newOrUpdatedCategory.defaultIntensity !== "") {
      emptyLibrary.categories[0].defaultIntensity =
        newOrUpdatedCategory.defaultIntensity;
    }
    if (newOrUpdatedCategory.defaultFrequency !== "") {
      emptyLibrary.categories[0].defaultFrequency =
        newOrUpdatedCategory.defaultFrequency;
    }

    if (newOrUpdatedCategory.documents !== "") {
      emptyLibrary.categories[0].documents = newOrUpdatedCategory.documents;
    }

    //Add thew newly created group, and the category to the library
    currentLib.groups.push(emptyLibrary);

    //return the full library
    return currentLib;
  } else {
    let i_group = grpIdx;
    let newInstruction = {
      categoryId: UUID(),
      categoryName: newOrUpdatedCategory.name,
      groupName: newOrUpdatedCategory.group,
      expanded: false,
      categoryDesc: newOrUpdatedCategory.description,
      categoryDescWithFormat: newOrUpdatedCategory.descriptionWithFormat,
      createdAt: { nanos: 0, seconds: new Date().getTime() },
      modifiedAt: { nanos: 0, seconds: new Date().getTime() },
    };
    if (newOrUpdatedCategory.imageUrl !== "") {
      newInstruction.imageUrl = newOrUpdatedCategory.imageUrl;
    }

    if (newOrUpdatedCategory.videoUrl !== "") {
      newInstruction.videoUrl = newOrUpdatedCategory.videoUrl;
    }

    if (newOrUpdatedCategory.youtubeVideo !== "") {
      newInstruction.youtubeVideo = newOrUpdatedCategory.youtubeVideo;
    }
    if (newOrUpdatedCategory.wistiaVideo !== "") {
      newInstruction.wistiaVideo = newOrUpdatedCategory.wistiaVideo;
    }

    if (newOrUpdatedCategory.defaultReps !== "") {
      newInstruction.defaultReps = newOrUpdatedCategory.defaultReps;
    }
    if (newOrUpdatedCategory.defaultSets !== "") {
      newInstruction.defaultSets = newOrUpdatedCategory.defaultSets;
    }
    if (newOrUpdatedCategory.defaultDuration !== "") {
      newInstruction.defaultDuration = newOrUpdatedCategory.defaultDuration;
    }
    if (newOrUpdatedCategory.defaultIntensity !== "") {
      newInstruction.defaultIntensity = newOrUpdatedCategory.defaultIntensity;
    }
    if (newOrUpdatedCategory.defaultFrequency !== "") {
      newInstruction.defaultFrequency = newOrUpdatedCategory.defaultFrequency;
    }

    if (newOrUpdatedCategory.documents !== "") {
      newInstruction.documents = newOrUpdatedCategory.documents;
    }
    //update the current group with the current instruction.
    currentLib.groups[i_group].categories.push(newInstruction);

    return currentLib;
  }
};

export const deleteLibrary = (appTemplate, deleteableLib) => {
  if (!appTemplate || !deleteableLib) {
    return null;
  }

  const updatedTemplate = deleteOld(appTemplate, deleteableLib);
  if (!updatedTemplate) {
    return null;
  }

  return {
    templateId: appTemplate.templateId,
    appId: appTemplate.appId,
    template: {
      header: updatedTemplate.header,
      groups: updatedTemplate.groups,
    },
  };
};

const getGroupIndexFromLibrary = (library, groupName) =>
  library.groups.map((group) => group.groupName).indexOf(groupName);

export const updateCurrentAppTemplateWithNewInstructionInfo = (
  newOrUpdatedInstruction,
  appTemplate
) => {
  let updatedAppTemplate = appTemplate;

  //see if instruction exists in template:

  return updatedAppTemplate;
};

const formatCategoryFromPageResp = (updatedCat, oldCat) => {
  let instruction = {
    categoryId: updatedCat.id,
    groupName: updatedCat.group,
    categoryName: updatedCat.name,
    categoryDesc: updatedCat.description,
    hasNewVideo:
      updatedCat.videoUrl !== "" && updatedCat.videoUrl !== oldCat.videoUrl,
    categoryDescWithFormat: updatedCat.descriptionWithFormat,
    createdAt: { nanos: 0, seconds: new Date().getTime() },
    modifiedAt: { nanos: 0, seconds: new Date().getTime() },
  };
  if (updatedCat.imageUrl !== "") {
    instruction.imageUrl = updatedCat.imageUrl;
  }

  if (updatedCat.videoUrl !== "") {
    instruction.videoUrl = updatedCat.videoUrl;
  }

  if (updatedCat.wistiaVideo && updatedCat.wistiaVideo.url !== "") {
    instruction.youtubeVideo = updatedCat.youtubeVideo;
  }
  if (updatedCat.wistiaVideo && updatedCat.wistiaVideo.url !== "") {
    instruction.wistiaVideo = updatedCat.wistiaVideo;
  }

  if (updatedCat.defaultReps !== "") {
    instruction.defaultReps = updatedCat.defaultReps;
  }
  if (updatedCat.defaultSets !== "") {
    instruction.defaultSets = updatedCat.defaultSets;
  }
  if (
    updatedCat.defaultDuration !== "" &&
    updatedCat.defaultDuration !== undefined
  ) {
    instruction.defaultDuration = updatedCat.defaultDuration;
  }
  if (
    updatedCat.defaultIntensity !== "" &&
    updatedCat.defaultIntensity !== undefined
  ) {
    instruction.defaultIntensity = updatedCat.defaultIntensity;
  }
  if (
    updatedCat.defaultFrequency !== "" &&
    updatedCat.defaultFrequency !== undefined
  ) {
    instruction.defaultFrequency = updatedCat.defaultFrequency;
  }

  if (updatedCat.documents && updatedCat.documents.length > 0) {
    instruction.documents = updatedCat.documents;
  }

  return instruction;
};



// WEBPACK FOOTER //
// ./src/services/middleware.js